/**
 * Draw a checkout rate exactly as the customer sees it — logo + localised
 * second line — by reusing the checkout extension's OWN modules.
 *
 * Nothing here reimplements the extension. `resolveMark` decides which mark and
 * which sentence a service_code earns, `renderSentence` builds the wording, the
 * locale JSONs are the same files `npm test` checks for key parity, and MARKS is
 * the generated data-URI bundle.
 *
 * Those modules are COPIED into src/vendor/carrier-marks by
 * scripts/sync-carrier-marks.mjs rather than imported across the repo: the
 * frontend Docker image only copies `frontend/`, so a relative import into
 * ../../extensions builds fine on a dev machine and breaks the deploy.
 * `npm run check-carrier-marks` fails if the copy drifts from the extension.
 *
 * The one piece the extension gets from Shopify and we have to supply is
 * `translate(key, options)`: Shopify's i18n, reduced to what the locale files
 * use — `{{var}}` interpolation and one/other plurals.
 */
import { resolveMark, CARRIER_LABEL } from '../vendor/carrier-marks/resolve';
import { renderSentence, renderAlt } from '../vendor/carrier-marks/sentence';
import { MARKS } from '../vendor/carrier-marks/marks';

import en from '../vendor/carrier-marks/locales.en.default.json';
import nl from '../vendor/carrier-marks/locales.nl.json';
import fr from '../vendor/carrier-marks/locales.fr.json';
import de from '../vendor/carrier-marks/locales.de.json';
import es from '../vendor/carrier-marks/locales.es.json';
import it from '../vendor/carrier-marks/locales.it.json';
import ptPT from '../vendor/carrier-marks/locales.pt-PT.json';
import pl from '../vendor/carrier-marks/locales.pl.json';
import da from '../vendor/carrier-marks/locales.da.json';

const LOCALES = { en, nl, fr, de, es, it, pt: ptPT, 'pt-PT': ptPT, pl, da };

/** 'nl-BE' -> 'nl'; unknown -> 'en' (what the extension falls back to). */
export function localeLanguage(locale) {
  if (!locale) return 'en';
  const lang = String(locale).trim().replace('_', '-');
  if (LOCALES[lang]) return lang;
  const base = lang.split('-')[0].toLowerCase();
  return LOCALES[base] ? base : 'en';
}

/** Shopify's translate(), reduced to what the locale files actually use. */
export function makeTranslate(locale) {
  const dict = LOCALES[localeLanguage(locale)] || en;
  return (key, options = {}) => {
    let value = dict[key];
    if (value === undefined) return key;
    if (value && typeof value === 'object') {
      // plural group: pick one/other on `count`
      value = Number(options.count) === 1 ? value.one : value.other;
    }
    return String(value).replace(/\{\{(\w+)\}\}/g, (_, name) =>
      options[name] !== undefined ? String(options[name]) : '');
  };
}

/**
 * What the extension would draw for one rate.
 * Returns {mark: {src,w,h} | null, sentence: string | null, alt: string} or
 * null when the extension draws nothing for this rate.
 */
export function previewMark({ serviceCode, description, countryCode, locale }) {
  const resolved = resolveMark({
    code: serviceCode, description, countryCode, countries: new Set(),
  });
  if (!resolved) return null;
  const translate = makeTranslate(locale);
  return {
    mark: MARKS[resolved.mark] || null,
    sentence: renderSentence(translate, resolved.sentence),
    alt: renderAlt(translate, resolved, CARRIER_LABEL[resolved.carrier]),
    carrier: resolved.carrier,
  };
}
